import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { ConsentAttemptTelemetryService } from '../../../services/consent-attempt-telemetry.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  title = 'app';
  toggleNavbar;
  public lead;
  private recordedConsentRoutes = new Set<string>();

  constructor(
    private router: Router,
    private consentTelemetry: ConsentAttemptTelemetryService,
  ) {
  }

  ngOnInit() {
    try {
      this.recordConsentRouteActivated(window.location.pathname + window.location.search);
    } catch (e) { /* non-fatal diagnostics only */ }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => this.recordConsentRouteActivated(event.urlAfterRedirects || event.url));
  }

  private recordConsentRouteActivated(url: string): void {
    const urlWithoutFragment = (url || '').split('#')[0];
    const queryStart = urlWithoutFragment.indexOf('?');
    const pathname = queryStart >= 0 ? urlWithoutFragment.slice(0, queryStart) : urlWithoutFragment;
    const queryString = queryStart >= 0 ? urlWithoutFragment.slice(queryStart + 1) : '';

    //FRU-23038: the consent-update email-verification link mirrors the /consent/:id
    //activation diagnostics, but only when it is an update link (context=update);
    //a plain verification link records nothing. The regexes match the router URL
    //(/email-verification/:id) and the full pathname (/communication/email-verification/:id).
    const consentMatch = pathname.match(/(?:^|\/)consent\/([^/]+)$/);
    const updateMatch = consentMatch ? null : pathname.match(/(?:^|\/)email-verification\/([^/]+)$/);
    const match = consentMatch || updateMatch;
    if (!match || !match[1]) { return; }
    if (updateMatch && !this.isConsentUpdateContext(queryString)) { return; }

    let encryptedLeadId = '';
    try {
      encryptedLeadId = decodeURIComponent(match[1]);
    } catch (e) { return; }
    //The route type joins the dedupe key: the same encrypted lead id can legitimately be
    //measured on both surfaces in one session, while the initial-load call and the
    //NavigationEnd for the same navigation still record only once.
    const routeKey = (updateMatch ? 'email_verification_update' : 'consent') + ':' + encryptedLeadId;
    if (this.recordedConsentRoutes.has(routeKey)) { return; }

    const sourceMatch = queryString.match(/(?:^|&)src=([^&]+)/);
    let source = 'web';
    try {
      source = sourceMatch ? decodeURIComponent(sourceMatch[1]).toLowerCase() : 'web';
    } catch (e) { source = 'web'; }
    const channel = ['sms', 'email'].includes(source) ? source : 'web';

    this.consentTelemetry.startAttempt(encryptedLeadId, channel);
    const event: any = {
      stage: 'consent_route_activated',
      outcome: 'success',
    };
    if (updateMatch) { event.source = 'consent_update'; }
    this.consentTelemetry.record(encryptedLeadId, event);
    this.recordedConsentRoutes.add(routeKey);
  }

  private isConsentUpdateContext(queryString: string): boolean {
    const contextMatch = queryString.match(/(?:^|&)context=([^&]*)/);
    if (!contextMatch) { return false; }
    try {
      return decodeURIComponent(contextMatch[1]) === 'update';
    } catch (e) { return false; }
  }
}
